import React, { useState } from 'react'
import {
  Box,
  Button,
  Container,
  Divider,
  Tab,
  Tabs,
  Typography,
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { useReactiveVar } from '@apollo/client'
import { EnergyBusFragment } from '../../generated/graphql'
import { useEnergyBus } from '../../energy-bus/use-energy-bus'
import { useAlert } from '../alert/AlertProvider'
import {
  activeVersionVar,
  calculatorVar,
  projectVar,
} from '../../apollo/reactive-vars'
import { EnergyBusBasicInfoTab } from './basic-info-tab/EnergyBusBasicInfoTab'
import { EnergyBusEnergySystemTab } from './energy-system-tab/EnergyBusEnergySystemTab'
import { EnergyBusBoreholeFieldTab } from './borehole-field-tab/EnergyBusBoreholeFieldTab'
import { useAuthorization } from '../../authorization/use-authorization'
import { isConfirmationValid } from '../../borehole-field-container/borehole-field-confirmation-validator'
import { LabelWithAlertBadge } from '../common/LabelWithAlertBadge'
import { EnergyBusResultKey } from '../../provider/result-categories'
import { createConfirmationData } from '../../borehole-field-container/borehole-field-data-creator'

type Props = {
  energyBus: EnergyBusFragment
  onClose: () => void
}

export function EnergyBusDrawerContent({
  energyBus,
  onClose,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const { alert } = useAlert()
  const { deleteEnergyBus } = useEnergyBus()
  const activeVersion = useReactiveVar(activeVersionVar)
  const project = useReactiveVar(projectVar)
  const [tab, setTab] = useState(0)
  const canEditProject = useAuthorization().canEditProject(project)
  const calculator = useReactiveVar(calculatorVar)

  if (!activeVersion) {
    return <></>
  }

  const energyBusCalculator = calculator?.findEnergyBus()
  if (!energyBusCalculator) {
    return <></>
  }

  const results = energyBusCalculator.getResultValues()

  const harvested = results[EnergyBusResultKey.HeatFromEnvironment]
  const wasteInput = results[EnergyBusResultKey.HeatToEnvironment]
  const currentBoreholeData = createConfirmationData(
    energyBus,
    harvested,
    wasteInput,
  )

  const onDelete = () => {
    alert(
      t('energyBus.deleteDialog.title'),
      t('energyBus.deleteDialog.message'),
      [
        { title: t('common.cancel') },
        {
          title: t(`energyBus.deleteDialog.confirm`),
          color: 'secondary',
          onPress: async () => {
            await deleteEnergyBus(activeVersion, energyBus.id)
            onClose()
          },
        },
      ],
    )
  }

  return (
    <Box>
      <Container>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mt={4}
          mb={2}
        >
          <Typography variant="h3">{t('energyBus.name')}</Typography>

          <Button
            variant="contained"
            onClick={onDelete}
            disabled={!canEditProject}
          >
            {t('energyBus.delete')}
          </Button>
        </Box>

        <Tabs value={tab} onChange={(_, index: number) => setTab(index)}>
          <Tab
            id="basic-info-tab"
            label={t('energyBus.tabs.basicInfo.title')}
          />
          <Tab
            id="energy-system-tab"
            label={t('energyBus.tabs.energySystem.title')}
          />
          <Tab
            id="borehole-field-tab"
            label={
              <LabelWithAlertBadge
                label={t('boreholeField.title')}
                showBadge={!isConfirmationValid(energyBus, currentBoreholeData)}
              />
            }
          />
        </Tabs>
      </Container>

      <Divider />

      {tab === 0 && <EnergyBusBasicInfoTab />}
      {tab === 1 && <EnergyBusEnergySystemTab energyBus={energyBus} />}
      {tab === 2 && <EnergyBusBoreholeFieldTab energyBus={energyBus} />}
    </Box>
  )
}
